import React, { useState } from "react";
import { postComment, voteComment } from "@/api";
import { THEORY } from "@/constants/testIds";

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "";
  }
};

// Flatten a nested comment tree into a list of {node, depth, ancestors[]}
// so we can render without a recursive component (some babel plugins choke
// on self-referencing JSX).
const flattenTree = (nodes, depth = 0, out = []) => {
  for (const n of nodes) {
    out.push({ node: n, depth });
    if (n.children && n.children.length) {
      flattenTree(n.children, depth + 1, out);
    }
  }
  return out;
};

const CommentRow = ({ entry, slug, onUpdate, replyOpenFor, setReplyOpenFor }) => {
  const { node, depth } = entry;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const isReplying = replyOpenFor === node.id;

  const submitReply = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await postComment(slug, text.trim(), node.id);
      setText("");
      setReplyOpenFor(null);
      onUpdate();
    } finally {
      setBusy(false);
    }
  };

  const vote = async (dir) => {
    try {
      await voteComment(node.id, dir);
      onUpdate();
    } catch {
      /* no-op */
    }
  };

  const indent = Math.min(depth, 8) * 18;

  return (
    <div style={{ marginLeft: indent }}>
      <div
        className={`comment ${node.deleted ? "deleted" : ""}`}
        data-testid={THEORY.commentItem(node.id)}
      >
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button
            data-testid={THEORY.commentUpvote(node.id)}
            className={`vote-btn ${node.user_vote === "up" ? "active up" : ""}`}
            onClick={() => vote("up")}
            aria-label="upvote"
          >
            {"\u25B2"}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>
            {node.score}
          </span>
          <button
            data-testid={THEORY.commentDownvote(node.id)}
            className={`vote-btn ${node.user_vote === "down" ? "active down" : ""}`}
            onClick={() => vote("down")}
            aria-label="downvote"
          >
            {"\u25BC"}
          </button>
          <span className="muted" style={{ fontSize: 12, marginLeft: "0.5rem" }}>
            {node.codename}
          </span>
          <span className="faint" style={{ fontSize: 11 }}>
            {"· "}
            {formatTime(node.created_at)}
          </span>
        </div>
        <div style={{ marginTop: "0.4rem", whiteSpace: "pre-wrap" }}>{node.body}</div>
        {!node.deleted && (
          <div style={{ marginTop: "0.35rem" }}>
            <button
              className="ghost"
              data-testid={THEORY.commentReply(node.id)}
              style={{ padding: "0.15rem 0.5rem", fontSize: 11 }}
              onClick={() => setReplyOpenFor(isReplying ? null : node.id)}
            >
              {isReplying ? "cancel" : "reply"}
            </button>
          </div>
        )}
        {isReplying && (
          <form
            onSubmit={submitReply}
            style={{ marginTop: "0.5rem", maxWidth: 560 }}
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="$ reply..."
              rows={3}
            />
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.4rem" }}>
              <button type="submit" disabled={busy || !text.trim()}>
                {busy ? "POSTING…" : "POST REPLY"}
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => setReplyOpenFor(null)}
              >
                CANCEL
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const CommentList = ({ tree, slug, onUpdate }) => {
  const [replyOpenFor, setReplyOpenFor] = useState(null);
  const flat = flattenTree(tree);
  return (
    <div>
      {flat.map((entry) => (
        <CommentRow
          key={entry.node.id}
          entry={entry}
          slug={slug}
          onUpdate={onUpdate}
          replyOpenFor={replyOpenFor}
          setReplyOpenFor={setReplyOpenFor}
        />
      ))}
    </div>
  );
};

export default CommentList;
